import { useState, FormEvent, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useBrand } from "../context/BrandContext";
import { useConfig } from "../context/ConfigContext";
import { track } from "../lib/analytics";
import { OAuthButtons, OAuthDivider } from "../components/OAuthButtons";
import {
  colors, fontSizes, radii, transitions,
  centeredPageStyle, authCardStyle,
  baseInputStyle, baseInputFocusStyle,
  primaryBtnStyle, primaryBtnHoverStyle,
} from "../styles/theme";

const COGNITO_ENDPOINT = "https://cognito-idp.ap-southeast-2.amazonaws.com/";

async function cognitoSignUp(email: string, password: string, clientId: string) {
  const res = await fetch(COGNITO_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-amz-json-1.1",
      "X-Amz-Target": "AWSCognitoIdentityProviderService.SignUp",
    },
    body: JSON.stringify({
      ClientId: clientId,
      Username: email,
      Password: password,
      UserAttributes: [{ Name: "email", Value: email }],
    }),
  });
  const body = await res.json();
  if (!res.ok) {
    const type: string = body.__type ?? "";
    const msg: string  = body.message ?? `Signup failed (${res.status})`;
    if (type === "UsernameExistsException") {
      throw new Error("EMAIL_EXISTS");
    }
    if (type === "InvalidPasswordException" || type === "InvalidParameterException") {
      throw new Error(`Password does not meet requirements: ${msg}`);
    }
    throw new Error(msg);
  }
  return body;
}

async function cognitoConfirm(email: string, code: string, clientId: string) {
  const res = await fetch(COGNITO_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-amz-json-1.1",
      "X-Amz-Target": "AWSCognitoIdentityProviderService.ConfirmSignUp",
    },
    body: JSON.stringify({
      ClientId: clientId,
      Username: email,
      ConfirmationCode: code,
    }),
  });
  const body = await res.json();
  if (!res.ok) {
    const type: string = body.__type ?? "";
    const msg: string  = body.message ?? `Confirmation failed (${res.status})`;
    if (type === "CodeMismatchException") throw new Error("CODE_MISMATCH");
    if (type === "ExpiredCodeException")  throw new Error("CODE_EXPIRED");
    if (type === "LimitExceededException") throw new Error("Too many attempts — please wait a moment and try again.");
    throw new Error(msg);
  }
}

async function cognitoResend(email: string, clientId: string) {
  const res = await fetch(COGNITO_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-amz-json-1.1",
      "X-Amz-Target": "AWSCognitoIdentityProviderService.ResendConfirmationCode",
    },
    body: JSON.stringify({ ClientId: clientId, Username: email }),
  });
  if (!res.ok) {
    const body = await res.json();
    throw new Error(body.message ?? "Could not resend code");
  }
}

type Stage = "signup" | "verify";

function errorBanner(msg: string) {
  return (
    <div style={{
      background: "rgba(201,64,64,0.12)",
      border: `1px solid ${colors.error}`,
      borderRadius: radii.sm,
      padding: "0.6rem 0.9rem",
      color: colors.error,
      fontSize: fontSizes.sm,
    }}>
      {msg}
    </div>
  );
}

function successBanner(msg: string) {
  return (
    <div style={{
      background: "rgba(76,175,136,0.12)",
      border: `1px solid ${colors.green}`,
      borderRadius: radii.sm,
      padding: "0.6rem 0.9rem",
      color: colors.green,
      fontSize: fontSizes.sm,
    }}>
      {msg}
    </div>
  );
}

export function FleetSignup() {
  const navigate        = useNavigate();
  const brand           = useBrand();
  const { clientId }    = useConfig();

  // #2446 — signup_start on mount
  useEffect(() => { track("signup_start"); }, []);

  const [stage,          setStage]          = useState<Stage>("signup");
  const [email,          setEmail]          = useState("");
  const [password,       setPassword]       = useState("");
  const [confirm,        setConfirm]        = useState("");
  const [code,           setCode]           = useState("");
  const [error,          setError]          = useState<string | null>(null);
  const [success,        setSuccess]        = useState<string | null>(null);
  const [loading,        setLoading]        = useState(false);
  const [resending,      setResending]      = useState(false);
  const [focusedField,   setFocusedField]   = useState<string | null>(null);
  const [btnHover,       setBtnHover]       = useState(false);

  const inputStyle = (field: string) =>
    focusedField === field ? baseInputFocusStyle : baseInputStyle;

  // ── Stage 1: Sign up ────────────────────────────────────────────────────────

  async function handleSignup(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      await cognitoSignUp(email, password, clientId);
      track("signup_complete");          // #2446
      sessionStorage.setItem("pending_verify_email", email);
      navigate("/signup/verify");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Sign-up failed";
      if (msg === "EMAIL_EXISTS") {
        setError("This email is already registered. Sign in or reset your password.");
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }

  // ── Stage 2: Verify ─────────────────────────────────────────────────────────

  async function handleVerify(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);
    try {
      await cognitoConfirm(email, code.trim(), clientId);
      navigate("/login", { state: { verified: true } });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Verification failed";
      if (msg === "CODE_MISMATCH") {
        setError("Wrong code — check your email and try again.");
      } else if (msg === "CODE_EXPIRED") {
        setError("Code has expired.");
        setSuccess(null);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    setError(null);
    setSuccess(null);
    setResending(true);
    try {
      await cognitoResend(email, clientId);
      setSuccess("New code sent — check your inbox.");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not resend code");
    } finally {
      setResending(false);
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  const header = (
    <div style={{ textAlign: "center", marginBottom: "0.4rem" }}>
      <img
        src={brand.logo_url}
        alt={brand.name}
        style={{ height: 64, marginBottom: "0.75rem" }}
      />
      <h2 style={{
        color: brand.accent_color,
        fontSize: fontSizes.xl,
        fontWeight: 700,
        margin: 0,
        letterSpacing: "-0.01em",
      }}>
        {brand.name}
      </h2>
      <p style={{ color: colors.textMuted, fontSize: fontSizes.sm, margin: "0.25rem 0 0" }}>
        {stage === "signup" ? "Create your account" : "Verify your email"}
      </p>
    </div>
  );

  if (stage === "verify") {
    return (
      <div style={centeredPageStyle}>
        <form onSubmit={handleVerify} style={authCardStyle}>
          {header}

          <p style={{ color: colors.textMuted, fontSize: fontSizes.sm, margin: 0 }}>
            We sent a 6-digit code to <strong style={{ color: colors.textBody }}>{email}</strong>.
            Enter it below to confirm your account.
          </p>

          {error   && errorBanner(error)}
          {success && successBanner(success)}

          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <label style={{ color: colors.textMuted, fontSize: fontSizes.sm, fontWeight: 500 }}>
              Verification code
            </label>
            <input
              type="text"
              inputMode="numeric"
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              required
              autoFocus
              onFocus={() => setFocusedField("code")}
              onBlur={() => setFocusedField(null)}
              style={{ ...inputStyle("code"), letterSpacing: "0.2em", textAlign: "center" }}
            />
          </div>

          <button
            type="submit"
            disabled={loading || code.length < 6}
            onMouseEnter={() => setBtnHover(true)}
            onMouseLeave={() => setBtnHover(false)}
            style={{
              ...(btnHover && !loading ? primaryBtnHoverStyle : primaryBtnStyle),
              marginTop: "0.3rem",
              opacity: loading || code.length < 6 ? 0.7 : 1,
              cursor: loading || code.length < 6 ? "not-allowed" : "pointer",
              transition: transitions.fast,
            }}
          >
            {loading ? "Verifying…" : "Confirm account"}
          </button>

          <div style={{ display: "flex", justifyContent: "center", gap: "0.5rem", flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={handleResend}
              disabled={resending}
              style={{
                background: "none",
                border: "none",
                color: colors.textMuted,
                fontSize: fontSizes.sm,
                cursor: resending ? "not-allowed" : "pointer",
                padding: 0,
                textDecoration: "underline",
                opacity: resending ? 0.6 : 1,
              }}
            >
              {resending ? "Sending…" : "Resend code"}
            </button>
            <span style={{ color: colors.textMuted, fontSize: fontSizes.sm }}>·</span>
            <Link
              to="/login"
              style={{ color: colors.textMuted, fontSize: fontSizes.sm, textDecoration: "underline" }}
            >
              Back to sign in
            </Link>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div style={centeredPageStyle}>
      <form onSubmit={handleSignup} style={authCardStyle}>
        {header}

        {/* #2316 — Google + GitHub one-click signup */}
        <OAuthButtons mode="signup" />
        <OAuthDivider />

        {error && errorBanner(error)}

        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          <label style={{ color: colors.textMuted, fontSize: fontSizes.sm, fontWeight: 500 }}>
            Email
          </label>
          <input
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
            onFocus={() => setFocusedField("email")}
            onBlur={() => setFocusedField(null)}
            style={inputStyle("email")}
          />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          <label style={{ color: colors.textMuted, fontSize: fontSizes.sm, fontWeight: 500 }}>
            Password
          </label>
          <input
            type="password"
            placeholder="••••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            onFocus={() => setFocusedField("password")}
            onBlur={() => setFocusedField(null)}
            style={inputStyle("password")}
          />
          <span style={{ color: colors.textMuted, fontSize: fontSizes.xs }}>
            Min. 8 characters, with uppercase, number, and symbol.
          </span>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          <label style={{ color: colors.textMuted, fontSize: fontSizes.sm, fontWeight: 500 }}>
            Confirm password
          </label>
          <input
            type="password"
            placeholder="••••••••••"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            onFocus={() => setFocusedField("confirm")}
            onBlur={() => setFocusedField(null)}
            style={{
              ...inputStyle("confirm"),
              ...(confirm && confirm !== password
                ? { border: `1px solid ${colors.error}` }
                : {}),
            }}
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          onMouseEnter={() => setBtnHover(true)}
          onMouseLeave={() => setBtnHover(false)}
          style={{
            ...(btnHover && !loading ? primaryBtnHoverStyle : primaryBtnStyle),
            marginTop: "0.3rem",
            opacity: loading ? 0.7 : 1,
            cursor: loading ? "not-allowed" : "pointer",
            transition: transitions.fast,
          }}
        >
          {loading ? "Creating account…" : "Create account"}
        </button>

        <p style={{ textAlign: "center", color: colors.textMuted, fontSize: fontSizes.xs, margin: 0 }}>
          Already signed up?{" "}
          <Link to="/signup/verify" style={{ color: colors.textMuted, textDecoration: "underline" }}>
            Verify your code →
          </Link>
        </p>

        <p style={{ textAlign: "center", color: colors.textMuted, fontSize: fontSizes.sm, margin: 0 }}>
          Already have an account?{" "}
          <Link to="/login" style={{ color: colors.goldSecondary, textDecoration: "none" }}>
            Sign in
          </Link>
        </p>
      </form>
    </div>
  );
}
